/**
 * Custom hook for managing moderator consultation chat state
 * Simplified version of usePersonaChat — no belief tracking, no reverse persuade, no proposal testing
 */
import { useState, useRef, useCallback } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export function useModeratorChat() {
  const [isActive, setIsActive] = useState(false);
  const [chatId, setChatId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [streamingContent, setStreamingContent] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const readSSEStream = useCallback(async (response, onEvent) => {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = JSON.parse(line.slice(6));
              onEvent(event);
            } catch (e) {
              console.error('Failed to parse SSE event:', e);
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }, []);

  const startChat = useCallback(async (sessionId) => {
    setError(null);
    setIsActive(true);
    setChatMessages([]);
    setStreamingContent('');

    try {
      const response = await fetch(
        `${API_BASE}/api/session/${sessionId}/moderator-chat/start`,
        { method: 'POST' }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to start moderator chat');
      }

      const data = await response.json();
      setChatId(data.chat_id);
    } catch (err) {
      setError(err.message);
      console.error('[ModeratorChat] Start error:', err);
    }
  }, []);

  const sendMessage = useCallback(async (sessionId, content) => {
    if (!chatId || isStreaming || !content.trim()) return;

    setIsStreaming(true);
    setStreamingContent('');
    setError(null);

    // Add user message immediately
    setChatMessages(prev => [...prev, { role: 'user', content: content.trim() }]);

    try {
      abortRef.current = new AbortController();
      const response = await fetch(
        `${API_BASE}/api/session/${sessionId}/moderator-chat/message`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ chat_id: chatId, content: content.trim() }),
          signal: abortRef.current.signal,
        }
      );

      if (!response.ok) throw new Error('Failed to send message');

      let accumulated = '';
      await readSSEStream(response, (event) => {
        switch (event.type) {
          case 'content_chunk':
            accumulated += event.content;
            setStreamingContent(accumulated);
            break;
          case 'chat_end':
            setChatMessages(prev => [...prev, { role: 'moderator', content: event.content }]);
            setStreamingContent('');
            break;
          case 'error':
            setError(event.message);
            break;
        }
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message);
        console.error('[ModeratorChat] Message error:', err);
      }
    } finally {
      setIsStreaming(false);
      setStreamingContent('');
    }
  }, [chatId, isStreaming, readSSEStream]);

  const closeChat = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    setIsActive(false);
    setChatId(null);
    setChatMessages([]);
    setStreamingContent('');
    setIsStreaming(false);
    setError(null);
  }, []);

  return {
    // State
    isActive,
    chatId,
    chatMessages,
    streamingContent,
    isStreaming,
    error,

    // Actions
    startChat,
    sendMessage,
    closeChat,
  };
}
